/**
 * VideoOutput.jsx
 * The Output side of the Video screen: what is being made, what came out, and
 * what to do with it next.
 *
 * Dependencies: services/api.js, utils/video.js, react-router-dom
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api.js';
import { Download, Edit, Film, Image as ImageIcon, Plus, Warning } from '../Icons.jsx';
import { ACTIVE, elapsedSeconds, formatSeconds, gbp } from '../../utils/video.js';

const STATUS_TEXT = {
  starting: 'Starting…',
  processing: 'Generating…',
  saving: 'Saving it to the library…',
};

export default function VideoOutput({ job, checkError, onCancel, onTweak }) {
  const [tab, setTab] = useState('preview');
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();
  const active = !!job && ACTIVE.includes(job.status);

  // A running clock, so a long video visibly has not stalled.
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);

  const tabButton = (id, label) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={`vid-tab${tab === id ? ' is-active' : ''}`}
      onClick={() => setTab(id)}
    >
      {label}
    </button>
  );

  return (
    <div className="vid-out">
      <div className="vid-out__head">
        <h2 className="vid__title">Output</h2>
        <div className="vid-tabs" role="tablist">
          {tabButton('preview', 'Preview')}
          {tabButton('json', 'JSON')}
        </div>
      </div>

      {!job && (
        <div className="vid-out__empty">
          <Film size={30} />
          <p>Your video will appear here. Write a prompt and press Run.</p>
        </div>
      )}

      {job && tab === 'json' && (
        <pre className="vid-json">
          {JSON.stringify(
            {
              id: job.id,
              model: job.model,
              status: job.status,
              prediction_id: job.prediction_id,
              input: job.params,
              estimate_pence: Math.round(job.estimate_pence * 100) / 100,
              created_at: job.created_at,
              completed_at: job.completed_at,
              media_id: job.media_id,
              error: job.error,
            },
            null,
            2,
          )}
        </pre>
      )}

      {job && tab === 'preview' && (
        <>
          {active && (
            <div className="vid-out__stage">
              <span className="vid-spinner" aria-hidden="true" />
              <p className="vid-out__status">{STATUS_TEXT[job.status]}</p>
              <p className="vid-field__hint">{formatSeconds(elapsedSeconds(job.created_at, now))} so far</p>
              {job.error && <p className="vid-field__hint">{job.error}</p>}
              {checkError && <p className="vid-field__hint">Couldn’t reach Replicate just now — trying again.</p>}
              {job.status !== 'saving' && (
                <button type="button" className="btn btn--sm" onClick={() => onCancel(job)}>
                  Cancel
                </button>
              )}
            </div>
          )}

          {job.status === 'saved' && job.media_id && (
            <>
              <video
                className="vid-out__video"
                src={api.media.rawUrl(job.media_id)}
                controls
                autoPlay
                loop
                playsInline
              />
              <p className="vid-out__meta">
                Generated in {formatSeconds(elapsedSeconds(job.created_at, job.completed_at ?? job.updated_at))} · ≈{' '}
                {gbp(job.estimate_pence)} · saved to the library
              </p>
            </>
          )}

          {(job.status === 'failed' || job.status === 'canceled') && (
            <div className="notice notice--error">
              <Warning />
              <span>{job.status === 'canceled' ? 'Cancelled.' : (job.error ?? 'This video could not be made.')}</span>
            </div>
          )}

          <div className="btn-row vid-out__actions">
            <button type="button" className="btn btn--sm" onClick={() => onTweak(job)}>
              <Edit size={15} /> Tweak it
            </button>

            {job.status === 'saved' && job.media_id && (
              <>
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  onClick={() => navigate(`/create?media=${job.media_id}`)}
                >
                  <Plus size={15} /> Use in a post
                </button>
                <a className="btn btn--sm" href={api.media.rawUrl(job.media_id)} download>
                  <Download size={15} /> Download
                </a>
                <button type="button" className="btn btn--sm" onClick={() => navigate('/library')}>
                  <ImageIcon size={15} /> Library
                </button>
              </>
            )}
          </div>

          <p className="vid-out__prompt">{job.prompt}</p>
        </>
      )}
    </div>
  );
}
